import { useEffect, useState } from 'react';
import * as deviceStore from '../db/deviceStore';
import tenantService, {
  hasAdvancedAnalyticsEntitlement,
  type TenantInfo,
} from '../services/tenantService';

export interface TenantEntitlements {
  tenantInfo: TenantInfo | null;
  hasAdvancedAnalytics: boolean;
  isLoading: boolean;
}

/**
 * Tenant entitlement snapshot for analytics gating.
 *
 * A warm memory/localStorage cache initializes state synchronously so the
 * layout does not flash. With an empty cache, `isLoading` stays true and
 * `hasAdvancedAnalytics` stays false until `getCurrentTenant` resolves, so
 * Geo/AI/Reports never paint before the check. Stats does not depend on this
 * flag. IndexedDB is used only when `getCurrentTenant` returns nothing, and
 * that result is applied together with the fetch so a cold cache cannot paint
 * advanced UI early.
 */
export function useTenantEntitlements(): TenantEntitlements {
  const [tenantInfo, setTenantInfo] = useState<TenantInfo | null>(() => tenantService.getCachedTenant());
  const [isLoading, setIsLoading] = useState(tenantInfo === null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        let deviceFallback: TenantInfo | null = null;
        if (tenantService.getCachedTenant() === null) {
          const rows = await deviceStore.listTenantInfo().catch(() => []);
          if (rows.length > 0 && tenantService.getCachedTenant() === null) {
            const row = rows[0];
            deviceFallback = {
              id: row.id,
              name: row.name,
              subdomain: row.subdomain,
              description: row.description,
              entitlements: row.entitlements,
            };
          }
        }

        const fresh = await tenantService.getCurrentTenant();
        if (cancelled) return;
        // Apply the network result when it arrives. IndexedDB is only used when
        // that fetch returns nothing, so a cold cache cannot paint advanced UI early.
        if (fresh) {
          setTenantInfo(fresh);
        } else if (deviceFallback) {
          setTenantInfo(deviceFallback);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  return {
    tenantInfo,
    hasAdvancedAnalytics: hasAdvancedAnalyticsEntitlement(tenantInfo),
    isLoading,
  };
}
